import { FindexClient } from '@/integrations/findex'
import { finsquid } from '@/integrations/finsquid'
import type {
  IFinsquidBankIDLoginResponse
} from '@/integrations/finsquid/types'
import {
  EAssetSubtype,
  assetShouldHaveAutomaticLogo,
  getRefId,
  type IntegrationProvider,
  type IntegrationUserAccount,
  type IProviderImport,
  type PotentialLiability
} from 'findef'
import { computed, ref, type ComputedRef, type Ref } from 'vue'
import { useQR } from './useQR'
import { parseLoginOptions } from '../utils/loginOptions'
import { getExternalIdV2, matchesExternalIdV2 } from '../utils/externalIdV2'
import { getErrorMessage } from '@/utils/error'
import { toggleItem } from '@/utils/array'
import { EventProducer } from '@/utils/event'
import { defineField, EFormEvent, useForm, Validators, type UseForm } from '@/form/useForm'
import type { ProviderImportFormState } from '../types/form'
import { useAnimationStep } from '@/utils/animation/useAnimationStep'
import { lerp, smoothstep, unique, uniqueBy } from 'math'
import type { IProviderImportError } from './types'
import { useFindexQuery } from '@/integrations/findex/query'
import { useResponsive } from '~/composables/useResponsive'

export type UseProviderImportProps = {
  providerId?: number
}

export type ImportDifferenceStats = {
  unChangedCount: number
  newCount: number
  deletedCount: number
  newInvestmentCount: number
  newLiabilityCount: number
  deletedInvestmentCount: number
  deletedLiabilityCount: number
}

export type UseProviderImport = {
  providers: Ref<IntegrationProvider[]>
  providerImport: Ref<IProviderImport>;
  provider: Ref<IntegrationProvider | undefined>;
  userAccounts: Ref<IntegrationUserAccount[]>;
  stats: Ref<ImportDifferenceStats>
  errors: Ref<IProviderImportError[]>
  failureStatus: Ref<IFinsquidBankIDLoginResponse | null>
  qrString: Ref<string | null | undefined>
  sidString: Ref<string | null | undefined>
  selectProvider: (providerId: number) => Promise<void>
  selectUserAccount: (userAccountId: string) => Promise<void>
  scanQR: (userId: string) => Promise<IFinsquidBankIDLoginResponse | null | undefined>;
  refreshProviderImport: () => Promise<void>;
  refreshProviderImportProgress: ComputedRef<number>;
  saveProgress: ComputedRef<number>;
  fetchUserAccounts: () => Promise<void>
  toggleInvestment: (investment: { positionId?: string; externalId: string; externalAccountId?: string }) => void
  toggleLiability: (liability: { positionId?: string; externalId: string; externalAccountId?: string }) => void
  setBankAccountInvestmentsSelected: (bankAccountId: string, selected: boolean) => void;
  setBankAccountLiabilitiesSelected: (bankAccountId: string, selected: boolean) => void;
  mutateLiability: (id: string, fun: (old: PotentialLiability) => PotentialLiability) => void;
  addUserControlledId: (id: string) => void;
  save: () => Promise<null | IProviderImport>;
  cancel: () => void;
  reset: () => Promise<void>;
  isSaving: Ref<boolean>;
  isFetchingProviders: Ref<boolean>
  isFetchingProviderImport: Ref<boolean>
  isFetchingUserAccounts: Ref<boolean>
  isRefreshingProviderImport: Ref<boolean>
  isSelectingUserAccount: Ref<boolean>
  isScanningQR: Ref<boolean>;
  selectedProviderId: Ref<number>;
  events: EventProducer<ProviderImportEventMap>;
  form: UseForm<ProviderImportFormState, {}>
}

export enum EProviderImportEvent {
  PROVIDER_SELECTED = 'PROVIDER_SELECTED',
  IMPORT_REFRESHED = 'IMPORT_REFRESHED',
  IMPORT_FETCHED = 'IMPORT_FETCHED',
  QR_STARTED = 'QR_STARTED',
  QR_FINISHED = 'QR_FINISHED',
  FETCHING_USER_ACCOUNTS = 'FETCHING_USER_ACCOUNTS',
  USER_ACCOUNTS_FETCHED = 'USER_ACCOUNTS_FETCHED',
  USER_ACCOUNT_SELECTED = 'USER_ACCOUNT_SELECTED',
  AUTHENTICATION_FINISHED = 'AUTHENTICATION_FINISHED',
  ERROR = 'ERROR',
  FAILED = 'FAILED',
  RESTART = 'RESTART',
  MAINTENANCE = 'MAINTENANCE'
}

export type ProviderImportEventMap = {
  [EProviderImportEvent.PROVIDER_SELECTED]: { providerId: number };
  [EProviderImportEvent.IMPORT_FETCHED]: {};
  [EProviderImportEvent.IMPORT_REFRESHED]: {};
  [EProviderImportEvent.QR_STARTED]: {};
  [EProviderImportEvent.QR_FINISHED]: {};
  [EProviderImportEvent.FETCHING_USER_ACCOUNTS]: {};
  [EProviderImportEvent.USER_ACCOUNTS_FETCHED]: { accounts: IntegrationUserAccount[] };
  [EProviderImportEvent.USER_ACCOUNT_SELECTED]: { userAccountId: string };
  [EProviderImportEvent.AUTHENTICATION_FINISHED]: { accounts: IntegrationUserAccount[] };
  [EProviderImportEvent.ERROR]: { error: string };
  [EProviderImportEvent.FAILED]: { status: IFinsquidBankIDLoginResponse };
  [EProviderImportEvent.RESTART]: {};
  [EProviderImportEvent.MAINTENANCE]: {};
}

const maxArray = <T = any>(a: T[], b: T[]): T[] => {
  if (a.length > b.length) return a;
  return b;
}

export const useProviderImport = (props: UseProviderImportProps): UseProviderImport => {
  const responsive = useResponsive()
  const providers = ref<IntegrationProvider[]>([])
  const userAccounts = ref<IntegrationUserAccount[]>([])
  const qrString = ref<string | null | undefined>(null)
  const sidString = ref<string | null | undefined>(null);
  const userId = ref<string | undefined | null>(null);
  const userAccountId = ref<string | undefined | null>(null);
  const selectedProviderId = ref<number>(-1);
  const isCanceled = ref<boolean>(false);
  const failureStatus = ref<IFinsquidBankIDLoginResponse | null>(null);

  const emitter = new EventProducer<ProviderImportEventMap>();

  const findexQuery = useFindexQuery();

  const providerImport = ref<IProviderImport>({
    available: {
      bankAccounts: []
    },
    newExternalIds: [],
    seenExternalIds: [],
    newExternalLiabilityIds: [],
    seenExternalLiabilityIds: [],
    previous: {
      selected: {
        investmentIds: [],
        liabilityIds: []
      }
    },
    selected: {
      investmentIds: [],
      liabilityIds: []
    },
    providerId: -1,
    runCount: 0,
    session: {
      connected: false
    }
  })
  const initialProviderImport = ref<IProviderImport>(
    JSON.parse(JSON.stringify(providerImport.value))
  )
  const differenceStats = ref<ImportDifferenceStats>({
    deletedCount: 0,
    newCount: 0,
    unChangedCount: 0,
    newInvestmentCount: 0,
    newLiabilityCount: 0,
    deletedInvestmentCount: 0,
    deletedLiabilityCount: 0
  });

  let didSetInitialProviderImport: boolean = false;

  const setInitialProviderImport = (providerImp: IProviderImport) => {
    if (didSetInitialProviderImport) return;
    const init = JSON.parse(JSON.stringify(providerImp));
    initialProviderImport.value = init;
    console.log("PROVIDER IMPORT", init)
    if (init.runCount === 0) {
      // first time, add all available bankaccounts as selected
      for (const bankAccount of providerImp.available.bankAccounts) {
        bankAccount.providerAccountId && setBankAccountInvestmentsSelected(bankAccount.providerAccountId, true)
        bankAccount.providerAccountId && setBankAccountLiabilitiesSelected(bankAccount.providerAccountId, true)
      }
    }
    didSetInitialProviderImport = true;
    console.log({ initial: init });
  }

  const getNextStats = () => {
    const prev = initialProviderImport.value
    const next = providerImport.value

    const prevInvestmentSelected = maxArray(
      prev?.selected?.investmentIds || [],
      prev?.previous?.selected?.investmentIds || []
    );
    const nextInvestmentSelected = next?.selected?.investmentIds || [];

    const prevLiabilitySelected = maxArray(
      prev?.selected?.liabilityIds || [],
      prev?.previous?.selected?.liabilityIds || []
    );
    const nextLiabilitySelected = next?.selected?.liabilityIds || [];

    // Extract unique externalIds from position IDs (format: "externalId::accountId")
    const extractUniqueExternalIds = (positionIds: string[]) => {
      return Array.from(new Set(positionIds.filter((posId) => !!posId).map((posId) => posId.split('::')[0])));
    };

    const getDiff = (prevIds: string[], nextIds: string[]) => {
      const unChangedCount = nextIds.filter((id) => prevIds.includes(id)).length;
      const deletedCount = prevIds.filter((id) => !nextIds.includes(id)).length;
      const newCount = nextIds.filter((id) => !prevIds.includes(id)).length;
      return {
        unChangedCount,
        deletedCount,
        newCount
      };
    };

    const prevInvestmentIds = extractUniqueExternalIds(prevInvestmentSelected);
    const nextInvestmentIds = extractUniqueExternalIds(nextInvestmentSelected);
    const prevLiabilityIds = extractUniqueExternalIds(prevLiabilitySelected);
    const nextLiabilityIds = extractUniqueExternalIds(nextLiabilitySelected);

    const investmentDiff = getDiff(prevInvestmentIds, nextInvestmentIds);
    const liabilityDiff = getDiff(prevLiabilityIds, nextLiabilityIds);

    const prevUniqueIds = [
      ...prevInvestmentIds.map((id) => `inv:${id}`),
      ...prevLiabilityIds.map((id) => `liab:${id}`)
    ];
    const nextUniqueIds = [
      ...nextInvestmentIds.map((id) => `inv:${id}`),
      ...nextLiabilityIds.map((id) => `liab:${id}`)
    ];

    const prevSelectedCount = prevUniqueIds.length;
    const nextSelectedCount = nextUniqueIds.length;

    const countDiff = prevSelectedCount - nextSelectedCount
    const unChangedCount = nextUniqueIds.filter((id) =>
      prevUniqueIds.includes(id)
    ).length
    const deletedCount = prevUniqueIds.filter(
      (id) => !nextUniqueIds.includes(id)
    ).length
    const newCount = nextUniqueIds.filter(
      (id) => !prevUniqueIds.includes(id)
    ).length

    console.log({
      prevSelectedCount,
      nextSelectedCount,
      countDiff,
      deletedCount,
      newCount,
      unChangedCount
    })

    return {
      deletedCount: deletedCount,
      newCount: newCount,
      unChangedCount: unChangedCount,
      newInvestmentCount: investmentDiff.newCount,
      newLiabilityCount: liabilityDiff.newCount,
      deletedInvestmentCount: investmentDiff.deletedCount,
      deletedLiabilityCount: liabilityDiff.deletedCount
    }
  }

  const updateStats = () => {
    differenceStats.value = getNextStats()
  }

  const provider = computed(() => (
    providerImport.value.provider ||
    providers.value.find(it => it.id === selectedProviderId.value)) as (IntegrationProvider | undefined)
  )
  const errors = ref<IProviderImportError[]>([])

  const onError = (e: unknown) => {
    if (isCanceled.value) return;
    console.log(isCanceled.value)
    console.error(e)
    const message = getErrorMessage(e, [
      'message',
      'reason',
      'error',
      'response',
      'cause',
      'stack',
      'trace'
    ])
    errors.value = [...errors.value, { err: e, reason: message }];
    emitter.emit(EProviderImportEvent.ERROR, { error: message });
  }

  const onFail = (status: IFinsquidBankIDLoginResponse) => {
    if (isCanceled.value) return;
    console.warn('BankID authentication failed:', status);
    failureStatus.value = status;
    emitter.emit(EProviderImportEvent.FAILED, { status });
  }

  const mutateLiability = (id: string, fun: (old: PotentialLiability) => PotentialLiability) => {
    for (const acc of (providerImport.value.available.bankAccounts || [])) {
      const old = (acc.liabilities || []).find(it => it.externalId === id);
      if (old) {
        const next = fun(old);
        old.quantity = next.quantity ?? 0;
        //diffAndPatch({ oldObject: old, newObject: next }, { compareNonNullishOnly: true });

        // first remove old mutation
        if (providerImport.value.mutated && providerImport.value.mutated.liabilities) {
          providerImport.value.mutated.liabilities = providerImport.value.mutated.liabilities.filter(it => it.externalId !== id);
        }

        // add new mutation
        providerImport.value.mutated = { ...(providerImport.value.mutated || {}), liabilities: uniqueBy([...(providerImport.value.mutated?.liabilities || []), next], it => it.externalId) };
        console.log('mutated!');
        break;
      }
    }
  }

  const addUserControlledId = (id: string) => {
    const ids = providerImport.value.userControlledIds || [];
    const nextIds = unique([...ids, id]);
    providerImport.value.userControlledIds = nextIds;
  }

  const isFetchingProviders = ref<boolean>(true)
  const isFetchingProviderImport = ref<boolean>(false)
  const isFetchingUserAccounts = ref<boolean>(false)
  const isRefreshingProviderImport = ref<boolean>(false);
  const isSaving = ref<boolean>(false);
  const isSelectingUserAccount = ref<boolean>(false)
  const isScanningQR = ref<boolean>(false)
  const upsert = findexQuery.mutations.useUpsertProviderImport();
  const run = findexQuery.mutations.useRunProviderImport();
  const shouldFetchProviderImport = computed(() => selectedProviderId.value > 0);
  const fetchImport = findexQuery.queries.useGetProviderImport(selectedProviderId, {
    enabled: shouldFetchProviderImport,
  });
  let logoRefreshTimeout: ReturnType<typeof setTimeout> | null = null;

  const hasMissingLogos = (imp: IProviderImport): boolean => {
    const accounts = imp.available?.bankAccounts || [];
    return accounts.some((acc) =>
      (acc.investments || []).some((inv) => {
        const asset = inv.asset;
        if (!asset) return false;
        if (asset.automaticLogoFailed) return false;
        if (!assetShouldHaveAutomaticLogo(asset)) return false;
        return !asset.image && !inv.image;
      }),
    );
  };

  const scheduleLogoRefresh = (attempt: number = 1) => {
    if (selectedProviderId.value <= 0) return;
    if (!hasMissingLogos(providerImport.value)) return;
    if (logoRefreshTimeout) {
      clearTimeout(logoRefreshTimeout);
      logoRefreshTimeout = null;
    }

    logoRefreshTimeout = setTimeout(async () => {
      try {
        if (selectedProviderId.value <= 0 || isCanceled.value) return;
        const [latestProviderImport] = await FindexClient.getProviderImport(
          selectedProviderId.value,
          finsquid.sid,
        );
        if (!latestProviderImport) return;
        providerImport.value = {
          ...providerImport.value,
          available: latestProviderImport.available,
        };
        updateStats();

        if (attempt < 3 && hasMissingLogos(providerImport.value)) {
          scheduleLogoRefresh(attempt + 1);
        }
      } catch (e) {
        console.error(e);
      } finally {
        logoRefreshTimeout = null;
      }
    }, Math.max(1200, attempt * 1200));
  };

  const fetchProviders = async () => {
    isFetchingProviders.value = true
    try {
      const [providerz] = await FindexClient.getFinsquidProviders();
      //const providerz = await finsquid.getProviders()
      if (providerz && Array.isArray(providerz)) {
        providers.value = providerz
      }
    } catch (e) {
      onError(e)
    }
    isFetchingProviders.value = false
  }

  fetchProviders().catch((e) => console.error(e))

  const fetchProviderImport = async (_providerId: number) => {
    if (_providerId <= 0) {
      isFetchingProviderImport.value = false;
      return;
    }

    isFetchingProviderImport.value = true;
    const resp = (await fetchImport.suspense()).data;
    console.log('RESP', resp);
    if (!resp) return
    const id = getRefId(resp);
    console.log('ID', id);
    if (!id || id === '') return;


    providerImport.value = { ...providerImport.value, ...resp }

      providerImport.value.selected.investmentIds =
        providerImport.value.selected.investmentIds.length > 0
          ? providerImport.value.selected.investmentIds
          : providerImport.value.previous.selected.investmentIds
      providerImport.value.selected.investmentIds =
        providerImport.value.selected.investmentIds.filter((it) => !!it);

    isFetchingProviderImport.value = false
    scheduleLogoRefresh()
  }

  const refreshProviderImportProgressAnim = useAnimationStep({
    initial: 0.0
  })
  const refreshProviderImportProgress = computed(() => lerp(0.0, 1.0, smoothstep(0.0, 1.0, refreshProviderImportProgressAnim.current.value)));

  const saveProgressAnim = useAnimationStep({
    initial: 0.0
  })
  const saveProgress = computed(() => lerp(0.0, 1.0, smoothstep(0.0, 1.0, saveProgressAnim.current.value)));

  const refreshProviderImport = async () => {
    isRefreshingProviderImport.value = true
    await refreshProviderImportProgressAnim.to(0.25);
    await refreshProviderImportProgressAnim.to(0.5);
    try {
      providerImport.value.providerId = selectedProviderId.value;
      const resp = await upsert.mutateAsync(providerImport.value);
      if (!resp) {
        await refreshProviderImportProgressAnim.to(1.0);
        return;
      }

      providerImport.value = { ...providerImport.value, ...resp }
      providerImport.value.selected.investmentIds =
        providerImport.value.selected.investmentIds.length > 0
          ? providerImport.value.selected.investmentIds
          : providerImport.value.previous.selected.investmentIds
      providerImport.value.selected.investmentIds =
        providerImport.value.selected.investmentIds.filter((it) => !!it);

      setInitialProviderImport(providerImport.value);
      updateStats();
      emitter.emit(EProviderImportEvent.IMPORT_REFRESHED, {});
      scheduleLogoRefresh()
    } catch (e) {
      onError(e)
    }
    await refreshProviderImportProgressAnim.to(0.5 + 0.25);
    await refreshProviderImportProgressAnim.to(1.0);
    isRefreshingProviderImport.value = false
  }

  const fetchUserAccounts = async () => {
    emitter.emit(EProviderImportEvent.FETCHING_USER_ACCOUNTS, {  });
    isFetchingUserAccounts.value = true
    try {
      const accs = await finsquid.getUserAccounts(false)
      userAccounts.value = accs;

      emitter.emit(EProviderImportEvent.USER_ACCOUNTS_FETCHED, { accounts: accs });
      const acc = accs[0];
      if (accs.length <= 1) {
        if (acc) {
          await selectUserAccount(acc.accountId);
        }
        emitter.emit(EProviderImportEvent.AUTHENTICATION_FINISHED, { accounts: accs });
      }
    } catch (e) {
      onError(e)
    }
    isFetchingUserAccounts.value = false
  }

  // 1. Select provider
  const selectProvider = async (providerId: number) => {
    selectedProviderId.value = providerId;

    // Finsquid availability gate: if the gateway is down/unpaid, show the
    // maintenance screen instead of starting the auth flow.
    const [status] = await FindexClient.getFinsquidStatus();
    if (!status?.available) {
      emitter.emit(EProviderImportEvent.MAINTENANCE, {});
      return;
    }

    emitter.emit(EProviderImportEvent.PROVIDER_SELECTED, { providerId });
    await fetchProviderImport(providerId);
    const provid = providers.value.find(it => it.id === providerId) || providerImport.value.provider;
    if (provid) {
      providerImport.value.provider = provid;
      providerImport.value.providerId = providerId;

      // Check if provider supports mobile device (same device login)
      const isMobile = responsive.isSmall.value;
      if (isMobile) {
        const loginOptions = provid.loginOptions || [];
        const supportsMobile = loginOptions.some(lo => {
          const devices = lo.loginDevice || [];
          // If no devices specified, assume it supports all devices
          // If devices specified, must include "same" for mobile support
          return devices.length === 0 || devices.includes('same');
        });

        if (!supportsMobile) {
          // Provider only supports "other" device (desktop)
          onFail({
            status: 'unsupported_device',
            autostartToken: undefined,
            imageChallengeData: undefined,
            qrCodeImage: undefined,
            raw: undefined,
            sid: undefined
          });
          return;
        }
      }

      // If provider has a loginOption with loginMethod 'bankid', start QR scan immediately.
      const hasBankIdLogin = (provid.loginOptions || []).some(lo => lo.loginMethod === 'bankid');
      if (hasBankIdLogin) {
        scanQR('').catch((e) => onError(e));
      }
    }
    emitter.emit(EProviderImportEvent.IMPORT_FETCHED, {});
  }
  if (props.providerId) {
    selectProvider(props.providerId).catch((e) => console.error(e))
  }

  // 2. ScanQR
  const qr = useQR({
    onError: onError,
    onFail: onFail
  })
  const scanQR = async (
    userId: string
  ): Promise<IFinsquidBankIDLoginResponse | null | undefined> => {
    const provid = provider.value
    if (!provid) return null

    userId = userId.trim()

    isScanningQR.value = true
    emitter.emit(EProviderImportEvent.QR_STARTED, {});
    try {
      const loginOptions = parseLoginOptions(
        userId,
        (provid.loginOptions || []).map((it) => ({ ...it, iconUrl: it.iconUrl || '' })),
        responsive.isSmall.value
      )
      const resp = await qr.scanQR(userId, provid.id, loginOptions, {
        onReceiveQR: (code) => {
          if (!code) return;
          qrString.value = code
        },
        onReceiveSID: (sid) => {
          if (!sid) return;
          sidString.value = sid;
          finsquid.setSID(sid);
          providerImport.value.session = {
            connected: true,
            id: sid
          }
        }
      })

      isScanningQR.value = false
      emitter.emit(EProviderImportEvent.QR_FINISHED, {});
      if (resp && resp?.status === 'complete') {
        await fetchUserAccounts();
      }
      return resp
    } catch (e) {
      onError(e)
    }
    isScanningQR.value = false
    emitter.emit(EProviderImportEvent.QR_FINISHED, {});
    return null
  }

  // 3. Select User account
  const selectUserAccount = async (userAccountId: string) => {
    isSelectingUserAccount.value = true
    try {
      await finsquid.selectUserAccount(userAccountId);
      emitter.emit(EProviderImportEvent.USER_ACCOUNT_SELECTED, { userAccountId });

      if (userAccounts.value.length > 1) {
        emitter.emit(EProviderImportEvent.AUTHENTICATION_FINISHED, { accounts: userAccounts.value });
      }
    } catch (e) {
      onError(e)
    }
    isSelectingUserAccount.value = false
  }

  const toggleInvestment = (investment: { positionId?: string; externalId: string; externalAccountId?: string }) => {
    // Find what ID is actually stored in the array for this investment (handles backward compatibility)
    const existingId = providerImport.value.selected.investmentIds.find(id =>
      matchesExternalIdV2(id, investment)
    );

    // Use existing ID if found (to remove it), otherwise generate new format ID (to add it)
    const idToToggle = existingId || getExternalIdV2(investment);

    providerImport.value.selected.investmentIds = toggleItem(
      providerImport.value.selected.investmentIds,
      idToToggle
    )
    providerImport.value.selected.investmentIds =
      providerImport.value.selected.investmentIds.filter((it) => !!it)

    updateStats()
  }

  const toggleLiability = (liability: { positionId?: string; externalId: string; externalAccountId?: string }) => {
    // Find what ID is actually stored in the array for this liability (handles backward compatibility)
    const existingId = providerImport.value.selected.liabilityIds.find(id =>
      matchesExternalIdV2(id, liability)
    );

    // Use existing ID if found (to remove it), otherwise generate new format ID (to add it)
    const idToToggle = existingId || getExternalIdV2(liability);

    providerImport.value.selected.liabilityIds = toggleItem(
      providerImport.value.selected.liabilityIds,
      idToToggle
    )
    providerImport.value.selected.liabilityIds = providerImport.value.selected.liabilityIds.filter(
      (it) => !!it
    )

    updateStats()
  }

  const setBankAccountInvestmentsSelected = (bankAccountId: string, selected: boolean) => {
    const bankAccount = providerImport.value.available.bankAccounts.find(
      (acc) => acc.providerAccountId === bankAccountId
    )
    if (!bankAccount) return onError(new Error(`No such bankAccount: ${bankAccountId}`))

    // Filter out loan investments
    const investments = bankAccount.investments
      .filter((inv) => !inv?.asset?.subtypes?.includes(EAssetSubtype.LOAN))

    if (selected) {
      // When selecting, add new format IDs
      const positionIds = investments.map((it) => getExternalIdV2(it))
      providerImport.value.selected.investmentIds = unique([
        ...providerImport.value.selected.investmentIds,
        ...positionIds
      ])
    } else {
      // When deselecting, find and remove existing IDs (handles backward compatibility)
      providerImport.value.selected.investmentIds =
        providerImport.value.selected.investmentIds.filter((storedId) =>
          !investments.some(inv => matchesExternalIdV2(storedId, inv))
        )
    }
    providerImport.value.selected.investmentIds =
      providerImport.value.selected.investmentIds.filter((it) => !!it);

    updateStats();
  }

  const setBankAccountLiabilitiesSelected = (bankAccountId: string, selected: boolean) => {
    const bankAccount = providerImport.value.available.bankAccounts.find(
      (acc) => acc.providerAccountId === bankAccountId
    )
    if (!bankAccount) return onError(new Error(`No such bankAccount: ${bankAccountId}`))

    const liabilities = bankAccount.liabilities;
    const touchedLiabilityIds = liabilities.map((it) => getExternalIdV2(it));

    // Mark bulk account selections as user-controlled so auto-preselect logic
    // does not re-add liabilities the user intentionally changed.
    providerImport.value.userControlledIds = unique([
      ...(providerImport.value.userControlledIds || []),
      ...touchedLiabilityIds
    ]);

    if (selected) {
      // When selecting, add new format IDs
      const positionIds = touchedLiabilityIds;
      providerImport.value.selected.liabilityIds = unique([
        ...providerImport.value.selected.liabilityIds,
        ...positionIds
      ])
    } else {
      // When deselecting, find and remove existing IDs (handles backward compatibility)
      providerImport.value.selected.liabilityIds =
        providerImport.value.selected.liabilityIds.filter((storedId) =>
          !liabilities.some(liab => matchesExternalIdV2(storedId, liab))
        )
    }
    providerImport.value.selected.liabilityIds =
      providerImport.value.selected.liabilityIds.filter((it) => !!it);

    updateStats();
  }

  const save = async (): Promise<null | IProviderImport> => {
    isSaving.value = true;
    saveProgressAnim.set(0.0);
    await saveProgressAnim.to(0.25);

    try {
      providerImport.value.providerId = selectedProviderId.value;
      const nextProviderImport = await run.mutateAsync(providerImport.value);
      if (!nextProviderImport) throw new Error(`Could not run provider import`);

      await saveProgressAnim.to(0.25 + 0.5);
      await saveProgressAnim.to(1.0);
      isSaving.value = false;
      return nextProviderImport;
    } catch (e) {
      onError(e);
    }

    await saveProgressAnim.to(0.5 + 0.25);
    await saveProgressAnim.to(1.0);
    isSaving.value = false;

    return null;
  }

  const form = useForm<ProviderImportFormState, {}>({
    schema: {
      userId: defineField({
        type: 'text',
        placeholder: '12 Numbers',
        label: 'Personal Number',
        validators: [Validators.personalNumber()],
      }),
      userAccountId: defineField({
        type: 'text',
        placeholder: 'Select User Account',
        label: 'User Account',
        component: 'select-dropdown',
        options: {
          __v_skip: true,
          search: { enabled: false },
          computedItems: computed(() =>
            userAccounts.value.map((acc) => {
              return {
                key: acc.accountId,
                label: acc.accountName || acc.accountId,
                value: acc.accountId
              }
            })
          )
        }
      })
    },
    layout: {}
  });

  emitter.subscribe(EProviderImportEvent.PROVIDER_SELECTED, () => {
    form.setLayout({
      render: 'userId'
    })
  });

  emitter.subscribe(EProviderImportEvent.USER_ACCOUNTS_FETCHED, (ev) => {
    if (ev.accounts.length > 1) {
      form.setLayout({
        render: 'userAccountId'
      })
    }
  });

  form.onFieldEvent('userId', EFormEvent.FIELD_VALUE_CHANGE, (ev) => {
    const fieldUserId = ev.field.value.value;
    if (!fieldUserId) return;
    userId.value = fieldUserId;
  });

  form.onFieldEvent('userAccountId', EFormEvent.FIELD_VALUE_CHANGE, (ev) => {
    const accId = ev.field.value.value;
    if (!accId) return;
    userAccountId.value = accId;
  });

  const cancel = async () => {
    qr.bankIdClient.stop()
    await qr.bankIdClient.cancel().catch(e => console.error(e))
    clearData()
  }

  const reset = async () => {
    await cancel()
    emitter.emit(EProviderImportEvent.RESTART, {})
  }

  const clearData = () => {
    errors.value = []
    qrString.value = null
    sidString.value = null
    userId.value = null
    userAccounts.value = []
    isSaving.value = false
    isFetchingProviders.value = false
    isFetchingProviderImport.value = false
    isFetchingUserAccounts.value = false
    isRefreshingProviderImport.value = false
    isSelectingUserAccount.value = false
    isScanningQR.value = false
    if (logoRefreshTimeout) {
      clearTimeout(logoRefreshTimeout);
      logoRefreshTimeout = null;
    }
    form.setFieldValue('userId', null)
    userAccountId.value = null
    selectedProviderId.value = -1
    providerImport.value = initialProviderImport.value
    providerImport.value.session = { connected: false }
    refreshProviderImportProgressAnim.to(0.0);
  }

  return {
    providerImport,
    providers,
    errors,
    failureStatus,
    selectProvider,
    selectUserAccount,
    scanQR,
    refreshProviderImport,
    refreshProviderImportProgress,
    saveProgress,
    fetchUserAccounts,
    qrString,
    sidString,
    toggleInvestment,
    toggleLiability,
    stats: differenceStats,
    isSaving,
    isFetchingProviders,
    isFetchingProviderImport,
    isFetchingUserAccounts,
    isRefreshingProviderImport,
    isSelectingUserAccount,
    isScanningQR,
    selectedProviderId,
    provider,
    userAccounts,
    mutateLiability,
    setBankAccountInvestmentsSelected,
    setBankAccountLiabilitiesSelected,
    addUserControlledId,
    events: emitter,
    save,
    cancel,
    reset,
    form
  }
}
